/**
 * Weekly Close Page
 * 
 * Module 3: Weekly Close + Feedback Visual System
 * A calm financial ritual for couples and families.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { toast } from "sonner";
import { 
  Calendar,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  MessageSquare,
  Sparkles,
  Loader2,
  RefreshCw,
  LayoutGrid,
  Wallet,
  AlertTriangle,
  Flag,
  ChevronRight
} from "lucide-react";
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine
} from "recharts";
import { ConversationPrompt } from "../components/privacy/ConversationPrompt";
import { CycleJournalCapture } from "../components/privacy/CycleJournalCapture";
import { RhythmProgress } from "../components/guidance/RhythmProgress";
import { BetaFeedbackPrompt } from "../components/feedback";
import api from "../lib/api";
import CalmInterventions from "../components/weekly-close/CalmInterventions";
import { WeatherSummary, useFinancialWeather } from "../components/FinancialWeather";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get current week boundaries (Monday-Sunday)
function getCurrentWeekBoundaries() {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  
  return {
    week_start: monday.toISOString().split('T')[0],
    week_end: sunday.toISOString().split('T')[0]
  };
}

// Format currency with calm styling
function formatCurrency(amount, currency = 'NZD') {
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  }).format(Math.abs(amount));
}

// Donut chart colors - calm, muted palette
const DONUT_COLORS = {
  bills: {
    accounted: '#1a4d3e',
    remaining: '#e5e7eb'
  },
  goals: {
    achieved: '#1a4d3e',
    in_progress: '#f59e0b',
    adjust: '#94a3b8'
  },
  budget: {
    on_track: '#1a4d3e',
    over: '#dc2626',
    under: '#3b82f6'
  }
};

// Budget bar chart colors
const BUDGET_COLORS = {
  budgeted: '#1a4d3e',
  actual: '#64748b'
};

// ============== State Components ==============

function StartState({ onStart, isStarting }) {
  const { week_start, week_end } = getCurrentWeekBoundaries();
  
  const formatDateRange = () => {
    const start = new Date(week_start);
    const end = new Date(week_end);
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-NZ', options)} - ${end.toLocaleDateString('en-NZ', options)}`;
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="weekly-close-start">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-6">
        <Calendar className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h2 className="font-heading text-2xl font-semibold mb-2">Your review & reset</h2>
      <p className="text-muted-foreground mb-2 max-w-md">
        A simple moment to bring everything back into place — based on your rhythm.
      </p>
      <p className="text-sm text-muted-foreground mb-8 flex items-center gap-2">
        <Clock className="w-4 h-4" />
        {formatDateRange()}
      </p>
      <Button 
        size="lg" 
        onClick={onStart}
        disabled={isStarting}
        className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
        data-testid="start-weekly-close-btn"
      >
        {isStarting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Starting...
          </>
        ) : (
          'Start your reset'
        )}
      </Button>
    </div>
  );
}

function ComputeState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="weekly-close-compute">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-6">
        <RefreshCw className="w-10 h-10 text-[#1a4d3e] animate-spin" />
      </div>
      <h2 className="font-heading text-xl font-semibold mb-2">Pulling transactions and lining things up...</h2>
      <p className="text-muted-foreground max-w-md">
        This usually takes just a moment.
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="weekly-close-error">
      <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-6">
        <AlertCircle className="w-10 h-10 text-amber-600" />
      </div>
      <h2 className="font-heading text-xl font-semibold mb-2">We couldn't load your review & reset</h2>
      <p className="text-muted-foreground mb-6 max-w-md">
        {message || "This looks like a connection issue — your data is safe. Try again."}
      </p>
      <div className="flex gap-3">
        <Button onClick={onRetry} variant="outline" data-testid="retry-btn">
          Retry
        </Button>
        <Button variant="ghost" asChild>
          <a href="mailto:support@arawealth.app">Contact support</a>
        </Button>
      </div>
    </div>
  );
}

function FinalizedState({ results, onViewDetails, interventions, debugMode, currency }) {
  // Handle empty finalization state
  const isEmpty = !results;
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="weekly-close-finalized">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-6">
        <CheckCircle2 className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      
      {isEmpty ? (
        <>
          <h2 className="font-heading text-2xl font-semibold mb-2">Your first reset starts here</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            Once you've added income and transactions, this becomes your moment to reset and stay on track.
          </p>
        </>
      ) : (
        <>
          <h2 className="font-heading text-2xl font-semibold mb-2">You're set</h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            Everything is in place for your next cycle.
          </p>
        </>
      )}
      
      {/* Beta Feedback Prompt */}
      <div className="mb-6 w-full max-w-md">
        <BetaFeedbackPrompt 
          pageKey="weekly_close" 
          actionKey="weekly_close_finalized"
        />
      </div>

      {/* Rhythm Progress */}
      <div className="mb-6 w-full max-w-md">
        <RhythmProgress />
      </div>
      
      <div className="flex gap-3">
        {!isEmpty && (
          <Button onClick={onViewDetails} className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90" data-testid="view-details-btn">
            View summary
          </Button>
        )}
        <Button variant="outline" asChild>
          {isEmpty ? <a href="/plan">Go to The Plan</a> : <a href="/dashboard">Back to overview</a>}
        </Button>
      </div>
      
      {/* Debug Mode: Show CalmInterventions preview */}
      {debugMode && interventions && interventions.length > 0 && (
        <div className="mt-8 w-full max-w-2xl">
          <CalmInterventions 
            interventions={interventions} 
            currency={currency} 
            debugMode={debugMode}
          />
        </div>
      )}
    </div>
  );
}

// ============== Donut Components ==============

function DonutTile({ title, percentage, subtitle, colors, testId }) {
  const data = [
    { name: 'filled', value: percentage },
    { name: 'empty', value: 100 - percentage }
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
      <h3 className="text-sm font-medium text-muted-foreground mb-2">{title}</h3>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={25}
                outerRadius={35}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
              >
                <Cell fill={colors.primary} />
                <Cell fill={colors.secondary} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div>
          <p className="text-2xl font-semibold">{Math.round(percentage)}%</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function CashPositionTile({ totals, currency }) {
  // Use LOCKED fields: cash_position = inflows - outflows
  const cashPosition = totals?.cash_position ?? totals?.net_cashflow ?? 0;
  const direction = totals?.cash_position_direction || 'neutral';
  const isPositive = direction === 'surplus' || (direction === 'neutral' && cashPosition >= 0);
  const inflows = totals?.inflows_total ?? 0;
  const outflows = totals?.outflows_total ?? 0;
  
  // Tone-based insight
  const toneInsight = cashPosition > 0
    ? "You stayed ahead this week — nice work"
    : cashPosition < 0
    ? "A bit more went out than came in — let's adjust next week"
    : "Balanced week — steady and controlled";
  
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="cash-position-tile">
      <h3 className="text-sm font-medium text-muted-foreground mb-2">This week</h3>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isPositive ? 'bg-[#1a4d3e]/10' : 'bg-amber-100'
        }`}>
          {isPositive ? (
            <ArrowUpRight className="w-5 h-5 text-[#1a4d3e]" />
          ) : (
            <ArrowDownRight className="w-5 h-5 text-amber-600" />
          )}
        </div>
        <div>
          <p className={`text-2xl font-semibold ${isPositive ? 'text-[#1a4d3e]' : 'text-amber-600'}`}>
            {isPositive ? '+' : '-'}{formatCurrency(cashPosition, currency)}
          </p>
          <p className="text-xs text-muted-foreground">
            Money in: {formatCurrency(inflows, currency)} | Money out: {formatCurrency(outflows, currency)}
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground/70 mt-2 italic" data-testid="tone-insight">{toneInsight}</p>
    </div>
  );
}

// ============== Available Pool Indicator ==============

function AvailablePoolIndicator({ availablePool, currency }) {
  if (!availablePool) return null;
  
  const { available_opening, available_change, available_closing } = availablePool;
  const isPositiveChange = available_change >= 0;
  const isPositiveClosing = available_closing >= 0;
  
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="available-pool-indicator">
      <h3 className="text-sm font-medium text-muted-foreground mb-3">Available Pool</h3>
      
      {/* Main closing balance */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm">Available next week</span>
        <span className={`text-lg font-semibold ${isPositiveClosing ? 'text-[#1a4d3e]' : 'text-amber-600'}`}>
          {formatCurrency(available_closing, currency)}
        </span>
      </div>
      
      {/* Change breakdown */}
      {available_change !== 0 && (
        <div className={`flex items-center gap-2 text-sm p-2 rounded-lg ${
          isPositiveChange ? 'bg-[#1a4d3e]/5 text-[#1a4d3e]' : 'bg-amber-50 text-amber-700'
        }`}>
          {isPositiveChange ? (
            <ArrowUpRight className="w-4 h-4" />
          ) : (
            <ArrowDownRight className="w-4 h-4" />
          )}
          <span>
            {isPositiveChange 
              ? `+${formatCurrency(available_change, currency)} added from under-plan categories`
              : `-${formatCurrency(Math.abs(available_change), currency)} from categories over plan`
            }
          </span>
        </div>
      )}
      
      {/* Opening balance context */}
      {available_opening !== 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          Started week with {formatCurrency(available_opening, currency)} available
        </p>
      )}
    </div>
  );
}

// ============== Overspend Pattern Prompt ==============

function OverspendPrompt({ overspendFlags, onAction }) {
  if (!overspendFlags || overspendFlags.length === 0) return null;
  
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-4" data-testid="overspend-prompt">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-amber-600" />
        </div>
        <div>
          <h3 className="font-medium text-amber-800">Categories needing attention</h3>
          <p className="text-sm text-amber-700 mt-1">
            These categories have been over plan for multiple weeks.
          </p>
        </div>
      </div>
      
      {overspendFlags.map((flag) => {
        // Support both new API contract and legacy fields
        const catName = flag.name || flag.category_name || flag.category_id;
        const weeks = flag.consecutive_weeks || 2;
        const message = flag.message_suggestion || `${catName} has been over plan for ${weeks} weeks.`;
        const actions = flag.suggested_actions || ['Adjust budget', 'Review spending'];
        const overspendAmount = flag.total_overspend || 0;
        
        return (
          <div key={flag.category_id} className="pl-11 space-y-2">
            <p className="text-sm font-medium text-amber-800">
              {catName} - {weeks} weeks over plan 
              {overspendAmount > 0 && (
                <span className="text-amber-600 ml-1">
                  (total: {formatCurrency(overspendAmount, 'NZD')})
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {actions.slice(0, 3).map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => onAction?.(flag.category_id, action)}
                  className="text-xs px-3 py-1.5 bg-white border border-amber-300 rounded-full hover:bg-amber-50 text-amber-700 transition-colors"
                  data-testid={`overspend-action-${flag.category_id}-${idx}`}
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============== Budget Graph ==============

function BudgetRealityGraph({ categories, currency, showAll, onToggleShowAll }) {
  // Take top 6 by absolute variance, or all if showAll
  const displayCategories = showAll 
    ? categories 
    : categories.slice(0, 6);

  // Support both new API contract and legacy field names
  const chartData = displayCategories.map(cat => {
    const catName = cat.name || cat.category_name || cat.category_id;
    const budget = cat.budget_amount ?? cat.budget_weekly ?? 0;
    const actual = cat.actual_amount ?? cat.actual_weekly ?? 0;
    const variance = cat.variance_amount ?? cat.variance ?? (actual - budget);
    
    return {
      name: catName.length > 12 ? catName.substring(0, 12) + '...' : catName,
      budget,
      actual,
      variance,
      is_over_budget: cat.is_over_budget ?? variance > 0
    };
  });

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="budget-reality-graph">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold">Budget Reality</h3>
        {categories.length > 6 && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onToggleShowAll}
            data-testid="toggle-show-all-btn"
          >
            {showAll ? 'Show less' : `View all (${categories.length})`}
            {showAll ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        )}
      </div>
      
      {chartData.length > 0 ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" tickFormatter={(v) => formatCurrency(v, currency)} />
              <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value) => formatCurrency(value, currency)}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend />
              <Bar dataKey="budget" name="Budget" fill={BUDGET_COLORS.budgeted} radius={[0, 4, 4, 0]} />
              <Bar dataKey="actual" name="Actual" fill={BUDGET_COLORS.actual} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <p>Not enough data yet</p>
        </div>
      )}
    </div>
  );
}

// ============== Variance Table ==============

function VarianceTable({ categories, currency }) {
  // Support both new API contract (is_over_budget) and legacy (status)
  const getStatusLabel = (cat) => {
    if (cat.is_over_budget !== undefined) {
      return cat.is_over_budget ? 'Needs attention' : 'On track';
    }
    switch (cat.status) {
      case 'over': return 'Needs attention';
      case 'under': return 'Under plan';
      default: return 'On track';
    }
  };

  const getStatusColor = (cat) => {
    if (cat.is_over_budget !== undefined) {
      return cat.is_over_budget ? 'text-amber-600' : 'text-[#1a4d3e]';
    }
    switch (cat.status) {
      case 'over': return 'text-amber-600';
      case 'under': return 'text-blue-600';
      default: return 'text-[#1a4d3e]';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="variance-table">
      <div className="p-4 border-b border-border">
        <h3 className="font-heading text-lg font-semibold">Category Details</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 text-sm font-medium">Category</th>
              <th className="text-right p-3 text-sm font-medium">Budget</th>
              <th className="text-right p-3 text-sm font-medium">Actual</th>
              <th className="text-right p-3 text-sm font-medium">Variance</th>
              <th className="text-center p-3 text-sm font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {categories.length > 0 ? categories.map((cat, idx) => {
              // Support both new and legacy field names
              const catName = cat.name || cat.category_name || cat.category_id;
              const budget = cat.budget_amount ?? cat.budget_weekly ?? 0;
              const actual = cat.actual_amount ?? cat.actual_weekly ?? 0;
              const variance = cat.variance_amount ?? cat.variance ?? (actual - budget);
              const isPositiveVariance = variance <= 0; // Under/on budget is positive
              
              return (
                <tr key={cat.category_id} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                  <td className="p-3 text-sm">{catName}</td>
                  <td className="p-3 text-sm text-right">{formatCurrency(budget, currency)}</td>
                  <td className="p-3 text-sm text-right">{formatCurrency(actual, currency)}</td>
                  <td className={`p-3 text-sm text-right font-medium ${isPositiveVariance ? 'text-[#1a4d3e]' : 'text-amber-600'}`}>
                    {variance > 0 ? '+' : ''}{formatCurrency(variance, currency)}
                  </td>
                  <td className={`p-3 text-sm text-center ${getStatusColor(cat)}`}>
                    {getStatusLabel(cat)}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={5} className="p-6 text-center text-muted-foreground">
                  <p className="mb-1">You haven't set this up yet</p>
                  <p className="text-xs text-muted-foreground/60">Set up budgets in <a href="/plan?tab=expenses" className="text-[#1a4d3e] hover:underline font-medium">The Plan</a> to track your spending here.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============== Goals Checkpoint ==============

function GoalsCheckpoint({ goals, onUpdateGoal }) {
  const statusOptions = [
    { value: 'achieved', label: 'Done', icon: '✓', color: 'bg-[#1a4d3e] text-white' },
    { value: 'in_progress', label: 'Still in motion', icon: '◐', color: 'bg-amber-100 text-amber-700' },
    { value: 'adjust', label: 'Adjusted', icon: '⚙', color: 'bg-slate-100 text-slate-600' }
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="goals-checkpoint">
      <h3 className="font-heading text-lg font-semibold mb-4">How did things go?</h3>
      
      {goals && goals.length > 0 ? (
        <div className="space-y-3">
          {goals.map((goal) => (
            <div key={goal.goal_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm font-medium">{goal.title || goal.goal_id}</span>
              <div className="flex gap-2">
                {statusOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => onUpdateGoal(goal.goal_id, opt.value)}
                    className={`px-2 py-1 text-xs rounded-md transition-colors ${
                      goal.status === opt.value 
                        ? opt.color 
                        : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                    }`}
                    data-testid={`goal-${goal.goal_id}-${opt.value}`}
                  >
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">
          You haven't set any goals for this week yet. Head to <a href="/plan" className="text-[#1a4d3e] hover:underline font-medium">The Plan</a> to set one up.
        </p>
      )}
    </div>
  );
}

// ============== Reflection Panel ==============

// ============== Income Review Panel ==============

function IncomeReviewPanel({ incomeData, currency }) {
  if (!incomeData) return null;
  
  const { 
    expected_total = 0, 
    received_total = 0, 
    pending_total = 0,
    missed_total = 0,
    events_expected = 0,
    events_received = 0,
    events_pending = 0,
    events_missed = 0,
    variance = 0
  } = incomeData;
  
  const hasIncomeActivity = expected_total > 0 || received_total > 0 || pending_total > 0;
  const isOnTrack = variance >= 0 || pending_total === 0;
  const hasLateIncome = pending_total > 0;
  const hasMissedIncome = missed_total > 0;
  
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="income-review-panel">
      <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center">
          <ArrowDownRight className="w-3.5 h-3.5 text-[#1a4d3e]" />
        </div>
        Income Review
      </h3>
      
      {hasIncomeActivity ? (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
              <p className="text-xs text-muted-foreground">Expected</p>
              <p className="font-semibold text-[#1a4d3e] tabular-nums">
                {formatCurrency(expected_total, currency)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{events_expected} event{events_expected !== 1 ? 's' : ''}</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
              <p className="text-xs text-muted-foreground">Received</p>
              <p className="font-semibold text-[#1a4d3e] tabular-nums">
                {formatCurrency(received_total, currency)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{events_received} received</p>
            </div>
          </div>
          
          {/* Late/Missing Income Alerts */}
          {(hasLateIncome || hasMissedIncome) && (
            <div className="space-y-2">
              {hasLateIncome && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      {events_pending} income{events_pending !== 1 ? 's' : ''} late
                    </p>
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 ml-6">
                    {formatCurrency(pending_total, currency)} expected but not yet received
                  </p>
                </div>
              )}
              
              {hasMissedIncome && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      {events_missed} income{events_missed !== 1 ? 's' : ''} missed
                    </p>
                  </div>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1 ml-6">
                    {formatCurrency(missed_total, currency)} did not arrive
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Difference */}
          <div className={`p-3 rounded-lg border ${
            isOnTrack 
              ? 'bg-[#1a4d3e]/5 border-[#1a4d3e]/20' 
              : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800/30'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm">Difference</span>
              <span className={`font-semibold tabular-nums ${
                variance >= 0 ? 'text-[#1a4d3e]' : 'text-amber-600'
              }`}>
                {variance >= 0 ? '+' : ''}{formatCurrency(variance, currency)}
              </span>
            </div>
            {!isOnTrack && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                Consider following up on late payments
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">No income this week yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add income sources to track expected payments
          </p>
        </div>
      )}
    </div>
  );
}

// ============== Allocation Review Panel ==============

function AllocationReviewPanel({ allocationData, currency }) {
  if (!allocationData) return null;
  
  const { 
    total_allocated = 0, 
    total_spent = 0, 
    remaining_budget = 0,
    variance = 0,
    variance_percent = 0,
    groups = [],
    over_budget_items = [],
    under_budget_items = []
  } = allocationData;
  
  const hasData = total_allocated > 0;
  const isOnTrack = variance >= 0;
  const hasOverBudget = over_budget_items.length > 0;
  
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="allocation-review-panel">
      <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <LayoutGrid className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
        </div>
        Plan Review
      </h3>
      
      {hasData ? (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
              <p className="text-xs text-muted-foreground">Planned</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                {formatCurrency(total_allocated, currency)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
              <p className="text-xs text-muted-foreground">Actual Spent</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
                {formatCurrency(total_spent, currency)}
              </p>
            </div>
          </div>
          
          {/* Over Budget Alerts */}
          {hasOverBudget && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {over_budget_items.length} item{over_budget_items.length !== 1 ? 's' : ''} over budget
                </p>
              </div>
              <div className="space-y-1 ml-6">
                {over_budget_items.slice(0, 3).map((item, idx) => (
                  <p key={idx} className="text-xs text-amber-700 dark:text-amber-300">
                    {item.name}: spent {formatCurrency(item.spent, currency)} of {formatCurrency(item.allocated, currency)}
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* Variance Summary */}
          <div className={`p-3 rounded-lg border ${
            isOnTrack 
              ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/30' 
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm">Budget Remaining</span>
              <span className={`font-semibold tabular-nums ${
                isOnTrack ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
              }`}>
                {isOnTrack ? '+' : ''}{formatCurrency(remaining_budget, currency)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isOnTrack 
                ? `${Math.abs(variance_percent)}% under budget` 
                : `${Math.abs(variance_percent)}% over budget`}
            </p>
          </div>
          
          {/* Group Breakdown */}
          {groups.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">By Group</p>
              {groups.map((g, idx) => {
                const groupVariance = g.allocated - g.spent;
                const isGroupOnTrack = groupVariance >= 0;
                return (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">{g.name}</span>
                    <span className={`tabular-nums ${isGroupOnTrack ? 'text-slate-700 dark:text-slate-300' : 'text-amber-600'}`}>
                      {formatCurrency(g.spent, currency)} / {formatCurrency(g.allocated, currency)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">You haven't set this up yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create a plan to track spending vs budget
          </p>
        </div>
      )}
    </div>
  );
}

function BucketReviewPanel({ bucketData, currency }) {
  if (!bucketData) return null;
  
  const { 
    spending_buckets = [], 
    bills_buckets = [], 
    savings_buckets = [],
    overspent_buckets = [],
    underfunded_bills = [],
    summary = {}
  } = bucketData;
  
  const hasData = spending_buckets.length > 0 || bills_buckets.length > 0 || savings_buckets.length > 0;
  const hasIssues = overspent_buckets.length > 0 || underfunded_bills.length > 0;
  
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid="bucket-review-panel">
      <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Wallet className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
        </div>
        Bucket Review
      </h3>
      
      {hasData ? (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900/50 text-center">
              <p className="text-xs text-muted-foreground">Spending</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                {spending_buckets.length}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900/50 text-center">
              <p className="text-xs text-muted-foreground">Bills</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                {bills_buckets.length}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900/50 text-center">
              <p className="text-xs text-muted-foreground">Savings</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                {savings_buckets.length}
              </p>
            </div>
          </div>
          
          {/* Overspent Buckets Alert */}
          {overspent_buckets.length > 0 && (
            <div className="p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800/30">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <p className="text-sm font-medium text-rose-800 dark:text-rose-200">
                  {overspent_buckets.length} bucket{overspent_buckets.length !== 1 ? 's' : ''} overspent
                </p>
              </div>
              <div className="space-y-1 ml-6">
                {overspent_buckets.slice(0, 3).map((bucket, idx) => (
                  <p key={idx} className="text-xs text-rose-700 dark:text-rose-300">
                    {bucket.name}: {formatCurrency(Math.abs(bucket.remaining_amount || 0), currency)} over
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* Underfunded Bills Alert */}
          {underfunded_bills.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {underfunded_bills.length} bill{underfunded_bills.length !== 1 ? 's' : ''} underfunded
                </p>
              </div>
              <div className="space-y-1 ml-6">
                {underfunded_bills.slice(0, 3).map((bill, idx) => (
                  <p key={idx} className="text-xs text-amber-700 dark:text-amber-300">
                    {bill.name}: {Math.round(bill.funding_progress || 0)}% funded, due in {bill.days_until_due || '?'} days
                  </p>
                ))}
              </div>
            </div>
          )}
          
          {/* All Good State */}
          {!hasIssues && (
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <p className="text-sm text-emerald-800 dark:text-emerald-200">
                  All buckets are on track
                </p>
              </div>
            </div>
          )}
          
          {/* Spending Buckets Detail */}
          {spending_buckets.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Spending Buckets</p>
              {spending_buckets.slice(0, 4).map((bucket, idx) => {
                const isOver = (bucket.remaining_amount || 0) < 0;
                return (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400">{bucket.name}</span>
                    <span className={`tabular-nums ${isOver ? 'text-rose-600' : 'text-slate-700 dark:text-slate-300'}`}>
                      {formatCurrency(bucket.spent_amount || 0, currency)} / {formatCurrency(bucket.allocated_amount || 0, currency)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Bills Buckets Detail */}
          {bills_buckets.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Bill Buckets</p>
              {bills_buckets.slice(0, 4).map((bucket, idx) => {
                const isUnder = (bucket.funding_progress || 0) < 80;
                return (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600 dark:text-slate-400">{bucket.name}</span>
                      {bucket.days_until_due !== null && bucket.days_until_due <= 7 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          {bucket.days_until_due}d
                        </span>
                      )}
                    </div>
                    <span className={`tabular-nums ${isUnder ? 'text-amber-600' : 'text-slate-700 dark:text-slate-300'}`}>
                      {Math.round(bucket.funding_progress || 0)}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">You haven't set this up yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create buckets to organize your money
          </p>
        </div>
      )}
    </div>
  );
}

function ReflectionPanel({ aiSummary, reflection, onReflectionChange, onOpenMentor }) {
  // Support both new AIInsightCard format and legacy format
  const summaryText = aiSummary?.short_summary_text || aiSummary?.short_reflection || '';
  const keyPoints = aiSummary?.key_points || [];
  const options = aiSummary?.options || [];
  const nextStep = aiSummary?.next_step || '';
  const moreOptionsAvailable = aiSummary?.more_options_available || false;
  
  // Fall back to legacy suggested_next_steps if key_points is empty
  const displayPoints = keyPoints.length > 0 ? keyPoints : (aiSummary?.suggested_next_steps || []);
  
  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-4" data-testid="reflection-panel">
      <h3 className="font-heading text-lg font-semibold">A moment to reflect</h3>
      
      {/* AI Summary */}
      {(summaryText || displayPoints.length > 0) && (
        <div className="p-4 bg-[#1a4d3e]/5 rounded-lg border border-[#1a4d3e]/10">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-[#1a4d3e]" />
            </div>
            <div className="flex-1">
              {/* Summary Text */}
              {summaryText && (
                <p className="text-sm leading-relaxed" data-testid="ai-summary-text">{summaryText}</p>
              )}
              
              {/* Key Points */}
              {displayPoints.length > 0 && (
                <ul className="mt-3 space-y-1" data-testid="ai-key-points">
                  {displayPoints.slice(0, 3).map((point, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-[#1a4d3e]">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              )}
              
              {/* Next Step */}
              {nextStep && (
                <p className="mt-3 text-sm text-[#1a4d3e] font-medium" data-testid="ai-next-step">
                  → {nextStep}
                </p>
              )}
              
              {/* Options (max 3) */}
              {options.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-options">
                  {options.slice(0, 3).map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        if (opt.action_key === 'open_mentor') {
                          onOpenMentor?.();
                        }
                        // Other actions can be handled here
                      }}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#1a4d3e]/10 text-[#1a4d3e] hover:bg-[#1a4d3e]/20 transition-colors"
                      data-testid={`ai-option-${opt.action_key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {moreOptionsAvailable && (
                    <span className="text-xs text-muted-foreground self-center">
                      + more options
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Journal Entry */}
      <div>
        <label className="block text-sm font-medium mb-2">
          How did this week feel?
        </label>
        <Textarea
          placeholder="A few thoughts about this week..."
          value={reflection}
          onChange={(e) => onReflectionChange(e.target.value)}
          className="min-h-[100px] resize-none"
          data-testid="reflection-textarea"
        />
      </div>
      
      {/* Talk to Mentor */}
      <Button 
        variant="outline" 
        onClick={onOpenMentor}
        className="w-full"
        data-testid="talk-to-mentor-btn"
      >
        <MessageSquare className="w-4 h-4 mr-2" />
        Talk it through
      </Button>
    </div>
  );
}

// ============== Main Review State ==============

function ReviewState({ results, sessionId, goals, reflection, incomeData, allocationData, bucketData, weather, onReflectionChange, onUpdateGoal, onOpenMentor, onFinalize, isFinalizing, onOverspendAction, currency, interventions, onInterventionUpdate, debugMode }) {
  const [showAllCategories, setShowAllCategories] = useState(false);
  
  // Support both new API contract and legacy fields
  const cash = results?.cash || results?.totals || {};
  const donuts = results?.donuts || {};
  const billsData = results?.bills || results?.donut_scores?.bills_score || {};
  const goalsData = results?.goals || results?.donut_scores?.goals_score || {};
  const budgetData = results?.budget || {};
  const patterns = results?.patterns || {};
  const reflectionContext = results?.reflection_context || results?.ai_summary || {};
  
  // Legacy fallback
  const { totals, donut_scores, budgets, available_pool, overspend_flags, ai_summary } = results || {};
  
  // Use the LOCKED donut score fields (new contract first, then legacy)
  const billsPercentage = donuts.bills_score ?? donut_scores?.bills_score?.bills_score ?? 100;
  const goalsPercentage = donuts.goals_score ?? donut_scores?.goals_score?.goals_score ?? 100;
  const budgetPercentage = donuts.budget_score ?? donut_scores?.budget_score?.budget_score ?? 100;
  
  // Get categories from new contract or legacy
  const categories = budgetData.categories || budgets?.categories || [];
  
  // Get overspend patterns (legacy - keeping for backwards compatibility)
  const overspendPatterns = patterns.overspend_patterns || overspend_flags || [];

  // Calculate Plan (allocation) percentage
  const planPercentage = allocationData && allocationData.total_allocated > 0
    ? Math.min(100, Math.max(0, Math.round(((allocationData.total_allocated - allocationData.total_spent) / allocationData.total_allocated) * 100)))
    : 100;
  const planOnTrack = !allocationData || allocationData.total_spent <= allocationData.total_allocated;

  return (
    <div className="space-y-6" data-testid="weekly-close-review">
      {/* Top Summary Row - 5 tiles for the financial pillars */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <CashPositionTile totals={cash.cash_position !== undefined ? cash : totals} currency={currency} />
        <DonutTile 
          title="Income" 
          percentage={incomeData ? Math.min(100, Math.round((incomeData.received_total / Math.max(incomeData.expected_total + incomeData.pending_total, 1)) * 100)) : 0}
          subtitle={incomeData?.events_received ? `${incomeData.events_received} received` : 'Not set up yet'}
          colors={{ primary: '#1a4d3e', secondary: '#e5e7eb' }}
          testId="income-donut"
        />
        <DonutTile 
          title="Plan" 
          percentage={planPercentage}
          subtitle={allocationData?.total_allocated > 0 
            ? `${formatCurrency(allocationData.remaining_budget, currency)} remaining`
            : 'Not set up yet'}
          colors={{ primary: planOnTrack ? '#1a4d3e' : '#f59e0b', secondary: '#e5e7eb' }}
          testId="plan-donut"
        />
        <DonutTile 
          title="Bills" 
          percentage={billsPercentage}
          subtitle={`${billsData.bills_accounted_count ?? donut_scores?.bills_score?.bills_accounted_count ?? 0}/${billsData.bills_due_count ?? donut_scores?.bills_score?.bills_due_count ?? 0} covered`}
          colors={{ primary: DONUT_COLORS.bills.accounted, secondary: DONUT_COLORS.bills.remaining }}
          testId="bills-donut"
        />
        <DonutTile 
          title="Goals" 
          percentage={goalsPercentage}
          subtitle={`${goalsData.goals_total ?? donut_scores?.goals_score?.goals_total ?? 0} goals tracked`}
          colors={{ primary: DONUT_COLORS.goals.achieved, secondary: '#e5e7eb' }}
          testId="goals-donut"
        />
      </div>
      
      {/* Available Pool Indicator */}
      <AvailablePoolIndicator availablePool={available_pool} currency={currency} />
      
      {/* Financial Weather Summary - Show outlook for next week */}
      {weather && (
        <div className="bg-card border border-border rounded-xl p-4" data-testid="weather-outlook">
          <h3 className="font-medium text-sm text-muted-foreground mb-3">Looking Ahead</h3>
          <WeatherSummary weather={weather} currency={currency} />
        </div>
      )}
      
      {/* Module 3C: Calm Interventions - Shows when there are realignment suggestions */}
      {interventions && interventions.length > 0 && (
        <CalmInterventions 
          interventions={interventions} 
          currency={currency} 
          onUpdate={onInterventionUpdate}
          debugMode={debugMode}
        />
      )}
      
      {/* Legacy Overspend Prompts (fallback if no interventions) */}
      {(!interventions || interventions.length === 0) && overspendPatterns.length > 0 && (
        <OverspendPrompt overspendFlags={overspendPatterns} onAction={onOverspendAction} />
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Charts & Tables */}
        <div className="lg:col-span-2 space-y-6">
          <BudgetRealityGraph 
            categories={categories}
            currency={currency}
            showAll={showAllCategories}
            onToggleShowAll={() => setShowAllCategories(!showAllCategories)}
          />
          <VarianceTable 
            categories={categories}
            currency={currency}
          />
          <GoalsCheckpoint 
            goals={goals}
            onUpdateGoal={onUpdateGoal}
          />
        </div>

        {/* Right Column - Reflection */}
        <div className="space-y-4">
          {/* Income Review Panel */}
          <IncomeReviewPanel 
            incomeData={incomeData}
            currency={currency}
          />
          
          {/* Allocation Review Panel */}
          <AllocationReviewPanel 
            allocationData={allocationData}
            currency={currency}
          />
          
          {/* Bucket Review Panel */}
          <BucketReviewPanel 
            bucketData={bucketData}
            currency={currency}
          />
          
          <ReflectionPanel 
            aiSummary={reflectionContext.short_summary_text ? reflectionContext : ai_summary}
            reflection={reflection}
            onReflectionChange={onReflectionChange}
            onOpenMentor={onOpenMentor}
          />
          
          {/* Emotional Close + Cycle Journal */}
          <div className="border-t border-border mt-2 pt-4">
            <CycleJournalCapture sessionId={sessionId} />
            <p className="text-xs text-muted-foreground/60 mt-3 mb-4 text-center">You don't need perfection — just clarity.</p>
          </div>

          {/* Finalize Button */}
          <Button 
            size="lg" 
            onClick={onFinalize}
            disabled={isFinalizing}
            className="w-full bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
            data-testid="finalize-weekly-close-btn"
          >
            {isFinalizing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Finishing...
              </>
            ) : (
              'Finish reset'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============== Debug Mock Data (Development Only) ==============

const MOCK_INTERVENTIONS = [
  {
    category_id: "groceries",
    category_name: "Groceries",
    consecutive_weeks: 3,
    avg_weekly_overspend: 47.50,
    total_overspend: 142.50,
    message: "Groceries has been running over plan for 3 weeks. This might be a sign the original estimate needs adjusting.",
    realignment_options: [
      {
        option_id: "increase_groceries",
        intervention_type: "increase_budget",
        category_id: "groceries",
        category_name: "Groceries",
        current_weekly: 230.77,
        suggested_weekly: 283.02,
        change_amount: 52.25,
        rationale: "Increase Groceries budget to $283/week to better match your actual spending patterns."
      },
      {
        option_id: "redistribute_groceries_from_utilities",
        intervention_type: "redistribute",
        category_id: "groceries",
        category_name: "Groceries",
        current_weekly: 230.77,
        suggested_weekly: 265.77,
        change_amount: 35.00,
        source_category_id: "utilities",
        source_category_name: "Utilities",
        rationale: "Move $35/week from Utilities (which has been underspent) to Groceries."
      },
      {
        option_id: "review_groceries",
        intervention_type: "review_spending",
        category_id: "groceries",
        category_name: "Groceries",
        current_weekly: 230.77,
        suggested_weekly: 230.77,
        change_amount: 0,
        rationale: "Keep the current budget and review Groceries transactions to find opportunities to adjust spending."
      }
    ]
  },
  {
    category_id: "transport",
    category_name: "Fuel & Transport",
    consecutive_weeks: 2,
    avg_weekly_overspend: 28.00,
    total_overspend: 56.00,
    message: "Your Fuel & Transport spending has consistently exceeded the plan. Would you like to explore some realignment options?",
    realignment_options: [
      {
        option_id: "increase_transport",
        intervention_type: "increase_budget",
        category_id: "transport",
        category_name: "Fuel & Transport",
        current_weekly: 80.00,
        suggested_weekly: 110.80,
        change_amount: 30.80,
        rationale: "Increase Fuel & Transport budget to $111/week to better match your actual spending patterns."
      },
      {
        option_id: "review_transport",
        intervention_type: "review_spending",
        category_id: "transport",
        category_name: "Fuel & Transport",
        current_weekly: 80.00,
        suggested_weekly: 80.00,
        change_amount: 0,
        rationale: "Keep the current budget and review Fuel & Transport transactions to find opportunities to adjust spending."
      }
    ]
  }
];

// Check for debug mode via URL parameter
const isDebugOverspend = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('debug') === 'overspend';
};

// ============== Main Page Component ==============

export function WeeklyCloseContent() {
  return <WeeklyCloseInner embedded />;
}

export default function WeeklyClosePage() {
  return <WeeklyCloseInner />;
}

function WeeklyCloseInner({ embedded = false }) {
  const { household } = useAuth();
  const navigate = useNavigate();
  const currency = household?.currency || 'NZD';
  
  // Debug mode detection
  const debugOverspend = isDebugOverspend();

  // State management
  const [state, setState] = useState('loading'); // loading, start, compute, review, finalized, error
  const [sessionId, setSessionId] = useState(null);
  const [results, setResults] = useState(null);
  const [goals, setGoals] = useState([]);
  const [reflection, setReflection] = useState('');
  const [incomeData, setIncomeData] = useState(null);
  const [allocationData, setAllocationData] = useState(null);
  const [bucketData, setBucketData] = useState(null);
  const [error, setError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [mentorOpen, setMentorOpen] = useState(false);
  const [interventions, setInterventions] = useState([]);
  const [financialWeather, setFinancialWeather] = useState(null);

  // Close Cycle state
  const [closeCycleTab, setCloseCycleTab] = useState('weekly');
  const [cycleGoals, setCycleGoals] = useState([]);
  const [closingGoal, setClosingGoal] = useState(null);
  const [closeForm, setCloseForm] = useState({ status: 'achieved', notes: '', next_description: '', next_amount: '' });

  // Check for existing session or latest finalized close
  useEffect(() => {
    if (!household) return;
    checkExistingSession();
    fetchFinancialWeather();
  }, [household]);

  const fetchFinancialWeather = async () => {
    if (!household?.household_id) return;
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/cashflow/weather`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setFinancialWeather(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch financial weather:', e);
    }
  };

  // Fetch goals for close cycle section
  const fetchCycleGoals = useCallback(async () => {
    if (!household?.household_id) return;
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/workflow-goals`,
        { credentials: 'include' }
      );
      if (res.ok) setCycleGoals((await res.json()).goals || []);
    } catch { /* silent */ }
  }, [household]);

  useEffect(() => { fetchCycleGoals(); }, [fetchCycleGoals]);

  const handleCloseCycle = async () => {
    if (!closingGoal) return;
    try {
      const res = await fetch(
        `${API_URL}/api/households/${household.household_id}/workflow-goals/close-cycle`,
        {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            goal_id: closingGoal.goal_id,
            status: closeForm.status,
            notes: closeForm.notes || null,
            next_goal_description: closeForm.next_description || null,
            next_goal_target_amount: closeForm.next_amount ? parseFloat(closeForm.next_amount) : null
          })
        }
      );
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      toast.success(data.next_goal ? 'Done — your next focus is set' : 'Done — goal closed');
      setClosingGoal(null);
      setCloseForm({ status: 'achieved', notes: '', next_description: '', next_amount: '' });
      fetchCycleGoals();
    } catch { toast.error('Could not close cycle. Please try again.'); }
  };

  const checkExistingSession = async () => {
    try {
      // Check for latest finalized close
      const latestResp = await api.get('/api/weekly-close/latest');
      if (latestResp.ok) {
        const latestData = await latestResp.json();
        if (latestData.results) {
          // Check if it's for the current week
          const { week_start } = getCurrentWeekBoundaries();
          if (latestData.week_start === week_start) {
            setResults(latestData.results);
            setSessionId(latestData.session_id);
            setState('finalized');
            return;
          }
        }
      }
      
      // No finalized close for this week, show start state
      setState('start');
    } catch (err) {
      console.error('Failed to check existing session:', err);
      setState('start');
    }
  };

  // Fetch calm interventions (Module 3C)
  const fetchInterventions = async () => {
    // Debug mode: inject mock interventions for visual testing
    if (debugOverspend) {
      console.log('[DEBUG] Injecting mock overspend interventions for visual testing');
      setInterventions(MOCK_INTERVENTIONS);
      return;
    }
    
    try {
      const resp = await api.interventions.get();
      if (resp.ok) {
        const data = await resp.json();
        setInterventions(data.interventions || []);
      }
    } catch (err) {
      console.debug('Failed to fetch interventions:', err);
    }
  };

  const handleStart = async () => {
    setIsStarting(true);
    setError(null);
    
    try {
      const { week_start, week_end } = getCurrentWeekBoundaries();
      
      const resp = await api.post('/api/weekly-close/start', { week_start, week_end });
      if (!resp.ok) {
        throw new Error('Failed to start weekly close');
      }
      
      const data = await resp.json();
      setSessionId(data.session_id);
      
      // If session already exists and was finalized
      if (data.existing && data.status === 'finalized') {
        setState('finalized');
        // Fetch the results
        const resultsResp = await api.get(`/api/weekly-close/${week_start}`);
        if (resultsResp.ok) {
          setResults(await resultsResp.json());
        }
        return;
      }
      
      // Move to compute state
      setState('compute');
      await handleCompute(data.session_id);
      
    } catch (err) {
      console.error('Start error:', err);
      setError(err.message);
      setState('error');
    } finally {
      setIsStarting(false);
    }
  };

  const handleCompute = async (sid) => {
    try {
      const resp = await api.post('/api/weekly-close/compute', { session_id: sid });
      if (!resp.ok) {
        throw new Error('Failed to compute weekly close');
      }
      
      const data = await resp.json();
      setResults(data);
      setState('review');
      
      // Fetch goals, interventions, income, allocation and bucket data
      await Promise.all([
        fetchGoals(),
        fetchInterventions(),
        fetchIncomeData(),
        fetchAllocationData(),
        fetchBucketData()
      ]);
      
    } catch (err) {
      console.error('Compute error:', err);
      setError(err.message);
      setState('error');
    }
  };

  const fetchGoals = async () => {
    try {
      const { week_start } = getCurrentWeekBoundaries();
      const resp = await api.get(`/api/households/${household.household_id}/weekly-goals/${week_start}`);
      if (resp.ok) {
        const data = await resp.json();
        setGoals(data.goals || []);
      }
    } catch (err) {
      console.debug('No goals found:', err);
    }
  };

  // Fetch income weekly check data
  const fetchIncomeData = async () => {
    try {
      const { week_start } = getCurrentWeekBoundaries();
      const resp = await api.get(`/api/households/${household.household_id}/income/weekly-check?week_start=${week_start}`);
      if (resp.ok) {
        const data = await resp.json();
        setIncomeData(data.this_week || null);
      }
    } catch (err) {
      console.debug('No income data:', err);
    }
  };

  // Fetch allocation weekly check data
  const fetchAllocationData = async () => {
    try {
      const { week_start, week_end } = getCurrentWeekBoundaries();
      const resp = await api.get(`/api/households/${household.household_id}/allocations/weekly-check?week_start=${week_start}&week_end=${week_end}`);
      if (resp.ok) {
        const data = await resp.json();
        setAllocationData(data);
      }
    } catch (err) {
      console.debug('No allocation data:', err);
    }
  };

  // Fetch bucket review data for weekly close
  const fetchBucketData = async () => {
    try {
      const resp = await api.get(`/api/households/${household.household_id}/buckets/weekly-review`);
      if (resp.ok) {
        const data = await resp.json();
        setBucketData(data);
      }
    } catch (err) {
      console.debug('No bucket data:', err);
    }
  };

  // Handler for when an intervention is applied
  const handleInterventionUpdate = async () => {
    // Refresh interventions and re-compute results
    await fetchInterventions();
    // Optionally re-compute to reflect updated budgets
    if (sessionId) {
      await handleCompute(sessionId);
    }
  };

  const handleUpdateGoal = async (goalId, status) => {
    try {
      const { week_start, week_end } = getCurrentWeekBoundaries();
      await api.post(`/api/households/${household.household_id}/weekly-goals/update-status`, {
        week_start,
        week_end,
        goal_id: goalId,
        status
      });
      
      // Update local state
      setGoals(prev => prev.map(g => 
        g.goal_id === goalId ? { ...g, status } : g
      ));
      
      toast.success('Got it');
    } catch (err) {
      console.error('Failed to update goal:', err);
      toast.error('Could not update goal. Please try again.');
    }
  };

  const handleFinalize = async () => {
    setIsFinalizing(true);
    
    try {
      const resp = await api.post('/api/weekly-close/finalize', {
        session_id: sessionId,
        reflection: reflection || null,
        user_choice: results?.reserve_logic?.recommended_options?.[0] || null
      });
      
      if (!resp.ok) {
        throw new Error('Failed to finalize weekly close');
      }
      
      const data = await resp.json();
      
      // Handle empty finalization gracefully
      if (data.finalised_empty) {
        toast.success(data.message || 'Nothing to finalise yet. Add some data first.');
        setState('finalized');
        setResults(null); // No results for empty finalization
        return;
      }
      
      setResults(data.results || results);
      setState('finalized');
      
      toast.success("You're set for your next cycle");
      
    } catch (err) {
      console.error('Finalize error:', err);
      toast.error('Could not finalize. Please try again.');
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setState('start');
  };

  const handleViewDetails = async () => {
    setState('review');
    // Fetch income, allocation, and bucket data when viewing details from finalized state
    await Promise.all([
      fetchIncomeData(),
      fetchAllocationData(),
      fetchBucketData()
    ]);
  };

  const handleOpenMentor = () => {
    // Navigate to mentor with context
    navigate('/mentor', { 
      state: { 
        context: 'weekly_close',
        summary: results?.ai_summary?.short_reflection 
      } 
    });
  };

  const handleOverspendAction = (categoryId, action) => {
    // Handle overspend prompt actions
    console.log('Overspend action:', categoryId, action);
    // For now, show a toast - in future, this could open a modal or navigate
    toast.info(`Action: ${action} for category`);
  };

  // Render based on state
  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-12 h-12 border-4 border-[#1a4d3e] border-t-transparent rounded-full animate-spin" />
          </div>
        );
      
      case 'start':
        return <StartState onStart={handleStart} isStarting={isStarting} />;
      
      case 'compute':
        return <ComputeState />;
      
      case 'review':
        return (
          <ReviewState 
            results={results}
            sessionId={sessionId}
            goals={goals}
            reflection={reflection}
            incomeData={incomeData}
            allocationData={allocationData}
            bucketData={bucketData}
            weather={financialWeather}
            onReflectionChange={setReflection}
            onUpdateGoal={handleUpdateGoal}
            onOpenMentor={handleOpenMentor}
            onFinalize={handleFinalize}
            isFinalizing={isFinalizing}
            onOverspendAction={handleOverspendAction}
            currency={currency}
            interventions={interventions}
            onInterventionUpdate={handleInterventionUpdate}
            debugMode={debugOverspend}
          />
        );
      
      case 'finalized':
        return (
          <FinalizedState 
            results={results} 
            onViewDetails={handleViewDetails}
            interventions={debugOverspend ? MOCK_INTERVENTIONS : interventions}
            debugMode={debugOverspend}
            currency={currency}
          />
        );
      
      case 'error':
        return <ErrorState message={error} onRetry={handleRetry} />;
      
      default:
        return null;
    }
  };

  const Wrap = embedded ? ({children}) => <>{children}</> : AppLayout;

  return (
    <Wrap>
      <div className="p-6 lg:p-8" data-testid="weekly-close-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold">Reset your week</h1>
          <p className="text-muted-foreground text-sm">Take a few minutes to reset, reflect, and move forward with clarity.</p>
        </div>

        {renderContent()}

        {/* ============ CLOSE CYCLE SECTION ============ */}
        <section className="mt-10 pt-8 border-t border-border" data-testid="close-cycle-section">
          <div className="flex items-center gap-2 mb-4">
            <Flag className="w-4 h-4 text-[#1a4d3e]" />
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Reflect & set your focus</h2>
          </div>

          {/* Cycle tabs */}
          <div className="flex gap-1 mb-4 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 w-fit">
            {['weekly', 'monthly', 'quarterly', 'annual'].map(c => (
              <button
                key={c}
                onClick={() => setCloseCycleTab(c)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  closeCycleTab === c
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
                data-testid={`close-tab-${c}`}
              >
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>

          {/* Goals to close */}
          {(() => {
            const filtered = cycleGoals.filter(g => g.cycle === closeCycleTab);
            if (filtered.length === 0) {
              return (
                <div className="bg-card border border-dashed border-border rounded-xl p-6 text-center" data-testid="no-cycle-goals">
                  <p className="text-sm text-muted-foreground">No {closeCycleTab} goals to close right now</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Set one up in The Plan first</p>
                </div>
              );
            }

            return (
              <div className="space-y-3">
                {filtered.map(g => (
                  <div key={g.goal_id} className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`close-goal-${g.goal_id}`}>
                    <div className="px-4 py-3 flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#1a4d3e] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{g.description}</p>
                        {g.target_amount != null && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums mt-0.5">
                            <span>Target: ${g.target_amount}</span>
                            {g.supported_total > 0 && (
                              <span className="text-[#1a4d3e]">Supported: ${g.supported_total}</span>
                            )}
                            {g.remaining_gap > 0 && (
                              <span className="text-amber-600">Gap: ${g.remaining_gap}</span>
                            )}
                          </div>
                        )}
                        {/* Supported by items */}
                        {g.supported_by && g.supported_by.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {g.supported_by.map((item, idx) => (
                              <span key={idx} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-slate-800 text-muted-foreground">
                                {item.type === 'bucket' ? 'Bucket' : 'Alloc'}: {item.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => { setClosingGoal(g); setCloseForm({ status: 'achieved', notes: '', next_description: '', next_amount: '' }); }}
                        data-testid={`close-goal-btn-${g.goal_id}`}
                      >
                        Review <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                    </div>

                    {/* Inline close form */}
                    {closingGoal?.goal_id === g.goal_id && (
                      <div className="border-t border-border px-4 py-4 bg-slate-50/50 dark:bg-slate-900/30 space-y-3" data-testid="close-form">
                        {/* Status */}
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1.5">How did this go?</p>
                          <div className="flex gap-2">
                            {[
                              { val: 'achieved', label: 'Done', color: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800' },
                              { val: 'in_progress', label: 'Still in motion', color: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800' },
                              { val: 'changed', label: 'Adjusted', color: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700' },
                            ].map(s => (
                              <button
                                key={s.val}
                                onClick={() => setCloseForm(p => ({ ...p, status: s.val }))}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                                  closeForm.status === s.val ? s.color + ' ring-1 ring-offset-1' : 'border-border text-muted-foreground hover:bg-muted'
                                }`}
                                data-testid={`close-status-${s.val}`}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Notes */}
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Notes (optional)</p>
                          <Textarea
                            value={closeForm.notes}
                            onChange={e => setCloseForm(p => ({ ...p, notes: e.target.value }))}
                            placeholder="Any reflections on this goal..."
                            className="min-h-[60px] text-sm"
                            data-testid="close-notes"
                          />
                        </div>

                        {/* Next goal */}
                        <div className="pt-2 border-t border-border/50">
                          <p className="text-xs font-medium text-muted-foreground mb-1.5">What's your focus for next week?</p>
                          <input
                            value={closeForm.next_description}
                            onChange={e => setCloseForm(p => ({ ...p, next_description: e.target.value }))}
                            placeholder="What do you want to focus on next?"
                            className="w-full h-8 rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="next-goal-input"
                          />
                          <input
                            type="number"
                            value={closeForm.next_amount}
                            onChange={e => setCloseForm(p => ({ ...p, next_amount: e.target.value }))}
                            placeholder="Target amount (optional)"
                            className="w-full h-8 rounded-md border border-input bg-background px-3 text-sm mt-2"
                            data-testid="next-goal-amount"
                          />
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setClosingGoal(null)}>Cancel</Button>
                          <Button size="sm" className="text-xs h-7" onClick={handleCloseCycle} data-testid="confirm-close-btn">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Done — next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </section>
      </div>
    </Wrap>
  );
}
